/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/* eslint-disable @typescript-eslint/consistent-type-imports, @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766; jsx required at runtime for @jsxRuntime classic */
import { jsx, css } from '@emotion/react';
import { Card } from '@atlaskit/smart-card';
import { UnsupportedBlock, UnsupportedInline, WidthConsumer } from '@atlaskit/editor-common/ui';
import type { EventHandlers } from '@atlaskit/editor-common/ui';

import { CardErrorBoundary } from './fallback';
import {
	SmartLinkDraggable,
	SMART_LINK_DRAG_TYPES,
	SMART_LINK_APPEARANCE,
} from '@atlaskit/editor-smart-link-draggable';
import type { RendererAppearance } from '../../ui/Renderer/types';
import { getCardClickHandler } from '../utils/getCardClickHandler';
import { getEventHandler } from '../../utils';
import type { SmartLinksOptions } from '../../types/smartLinksOptions';
import InlineCard from './inlineCard';
import AnalyticsContext from '@atlaskit/analytics-next/AnalyticsContext';
import type { DatasourceAdfView } from '@atlaskit/link-datasource';
import { DatasourceTableView } from '@atlaskit/link-datasource';
import { CardSSR } from '@atlaskit/smart-card/ssr';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import type { DatasourceAttributeProperties } from '@atlaskit/adf-schema/block-card';
import { token } from '@atlaskit/tokens';
import { calcBreakoutWidth, canRenderDatasource } from '@atlaskit/editor-common/utils';
import { usePortal } from '../../ui/Renderer/PortalContext';
import { RendererCssClassName } from '../../consts';

const datasourceCenterWrapperStyles = css({
	marginTop: token('space.150'),
	marginBottom: token('space.150'),
});

const datasourceContainerStyleWithMarginTop = css({
	borderRadius: `${token('radius.large', '8px')}`,
	border: `${token('border.width')} solid ${token('color.border')}`,
	overflow: 'hidden',
	marginTop: `${token('space.150')}`,
	marginBottom: `${token('space.150')}`,
});

// No vertical margin when inside center wrapper (wrapper has margin so it participates in collapse).
const datasourceContainerStyleNoVerticalMargin = css({
	borderRadius: `${token('radius.large', '8px')}`,
	border: `${token('border.width')} solid ${token('color.border')}`,
	overflow: 'hidden',
});

export default function BlockCard(props: {
	data?: object;
	datasource?: DatasourceAttributeProperties;
	eventHandlers?: EventHandlers;
	isNodeNested?: boolean;
	layout?: string;
	localId?: string;
	onSetLinkTarget?: (url: string) => '_blank' | undefined;
	portal?: HTMLElement;
	rendererAppearance?: RendererAppearance;
	smartLinks?: SmartLinksOptions;
	url?: string;
}): jsx.JSX.Element {
	const { url, data, eventHandlers, smartLinks, isNodeNested, localId, onSetLinkTarget } = props;
	const portal = usePortal(props);
	const { actionOptions } = smartLinks || {};

	// Card/CardSSR's onClick — (e, { destinationUrl?, url? })
	const onClick = getCardClickHandler(eventHandlers, url);
	// SmartCardEventClickHandler — (e, url?) => void — for CardErrorBoundary.
	// When the gate is off, fall back to the old behaviour (pass the same onClick as Card).
	const onConsumerClick = getEventHandler(eventHandlers, 'smartCard');

	const platform = 'web';

	const cardProps = {
		url,
		data,
		onClick,
		container: portal,
		isDatasource: !!props.datasource,
		actionOptions,
		CompetitorPrompt: smartLinks?.CompetitorPrompt,
	};

	const analyticsData = {
		attributes: {
			location: 'renderer',
		},
		// Below is added for the future implementation of Linking Platform namespaced analytic context
		location: 'renderer',
	};

	const onError = ({ err }: { err?: Error }) => {
		if (err) {
			throw err;
		}
	};

	if (props.datasource) {
		const views = props.datasource.views as DatasourceAdfView[];
		const tableView = views.find((view) => view.type === 'table');
		const shouldRenderDatasource = tableView && canRenderDatasource(props.datasource.id);

		if (shouldRenderDatasource) {
			const columns = tableView.properties?.columns;
			const visibleColumnKeys = columns?.map(({ key }) => key);

			// eslint-disable-next-line @atlassian/perf-linting/no-expensive-computations-in-render -- Ignored via go/ees017 (to be fixed)
			const columnCustomSizesEntries = columns
				?.filter((c): c is { key: string; width: number } => !!c.width)
				.map<[string, number]>(({ key, width }) => [key, width]);

			const columnCustomSizes = columnCustomSizesEntries?.length
				? Object.fromEntries<number>(columnCustomSizesEntries)
				: undefined;

			// eslint-disable-next-line @atlassian/perf-linting/no-expensive-computations-in-render -- Ignored via go/ees017 (to be fixed)
			const wrappedColumnKeys = columns?.filter((c) => c.isWrapped).map((c) => c.key);

			const { datasource, layout } = props;

			return (
				<AnalyticsContext data={analyticsData}>
					<CardErrorBoundary
						unsupportedComponent={UnsupportedInline}
						datasourceId={props.datasource.id}
						onSetLinkTarget={onSetLinkTarget}
						// Ignored via go/ees005
						// eslint-disable-next-line react/jsx-props-no-spreading
						{...cardProps}
						onClick={onConsumerClick}
					>
						<WidthConsumer>
							{({ width }) => {
								const useCenterWrapper = !isNodeNested;
								const datasourceDiv = (
									<div
										css={
											useCenterWrapper
												? datasourceContainerStyleNoVerticalMargin
												: datasourceContainerStyleWithMarginTop
										}
										data-testid="renderer-datasource-table"
										data-local-id={localId}
										style={{
											// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
											width: isNodeNested ? '100%' : calcBreakoutWidth(layout, width),
										}}
									>
										<DatasourceTableView
											datasourceId={datasource.id}
											parameters={datasource.parameters}
											visibleColumnKeys={visibleColumnKeys}
											columnCustomSizes={columnCustomSizes}
											wrappedColumnKeys={
												wrappedColumnKeys && wrappedColumnKeys.length > 0
													? wrappedColumnKeys
													: undefined
											}
											url={url}
										/>
									</div>
								);
								return useCenterWrapper ? (
									<div
										// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
										className={
											RendererCssClassName.BLOCK_CARD_DATASOURCE_CENTER_WRAPPER +
											' ' +
											RendererCssClassName.FLEX_CENTER_WRAPPER
										}
										css={datasourceCenterWrapperStyles}
									>
										{datasourceDiv}
									</div>
								) : (
									datasourceDiv
								);
							}}
						</WidthConsumer>
					</CardErrorBoundary>
				</AnalyticsContext>
			);
		}

		return <InlineCard data={data} url={url} />;
	}

	let cardComponent;
	if (
		smartLinks?.ssr &&
		url &&
		// Clean up feature gates when both are fully rolled out.
		// - platform_ssr_smartlink_cards: Controls SSR smart links for confluence (100% rolled out)
		// - jfp-magma-ssr-iv-editor-links: Controls SSR smart links for jira (at time of comment: 50% rolled out)
		// Once both gates are 100% rolled out, remove this condition entirely and make
		// smart link SSR behavior the default.
		// NOTE: This is a temporary solution to allow for the gradual rollout of SSR smart links for jira and keep it
		// fully rolled out for confluence since both gates target different products keep only one gate to control the behavior
		// isnt correct.
		(fg('platform_ssr_smartlink_cards') || fg('jfp-magma-ssr-iv-editor-links'))
	) {
		cardComponent = (
			<CardSSR
				appearance="block"
				platform={platform}
				// Ignored via go/ees005
				// eslint-disable-next-line react/jsx-props-no-spreading
				{...cardProps}
				url={url}
				onError={onError}
				hideIconLoadingSkeleton
			/>
		);
	} else {
		cardComponent = (
			<Card
				appearance="block"
				platform={platform}
				// Ignored via go/ees005
				// eslint-disable-next-line react/jsx-props-no-spreading
				{...cardProps}
				onError={onError}
			/>
		);
	}

	return (
		<SmartLinkDraggable
			url={url || ''}
			appearance={SMART_LINK_APPEARANCE.BLOCK}
			source={SMART_LINK_DRAG_TYPES.RENDERER}
		>
			<AnalyticsContext data={analyticsData}>
				<div
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
					className="blockCardView-content-wrap"
					data-block-card
					// eslint-disable-next-line @atlassian/perf-linting/no-expensive-computations-in-render -- Ignored via go/ees017 (to be fixed)
					data-card-data={data ? JSON.stringify(data) : undefined}
					data-card-url={url}
					data-local-id={localId}
				>
					<CardErrorBoundary
						unsupportedComponent={UnsupportedBlock}
						onSetLinkTarget={onSetLinkTarget}
						// Ignored via go/ees005
						// eslint-disable-next-line react/jsx-props-no-spreading
						{...cardProps}
						onClick={onConsumerClick}
					>
						{cardComponent}
					</CardErrorBoundary>
				</div>
			</AnalyticsContext>
		</SmartLinkDraggable>
	);
}
